import React, { useContext, useEffect, useState } from 'react';
import { Button, ButtonDropdownProps, Icon, Popover, SpaceBetween } from '@amzn/awsui-components-react';
import CONSTANTS from 'src/utils/constants';
import DomUtils from 'src/utils/DomUtils';
import FormulaExpressionToolbar from 'src/components/shared/FormulaExpressionToolbar';
import { TPEReactTable } from 'src/components/shared/TPEReactTable';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import { renderExpressionParts } from 'src/components/shared/FormulaExpressionFunctionComponents';
import AllocationGroup, { AllocationGroupFormula, FormulaMapping } from 'src/models/tp-allocation/AllocationGroup';
import { ACTIONS } from 'src/services/tp-allocation/allocation-groups/AllocationGroupsReducer';
import { DasAllocationGroupsContext } from 'src/services/derived-allocation-sheet/DasAllocationGroupsContext';
import { newAllocationGroupFormula, toAllocationGroupFormula } from 'src/services/derived-allocation-sheet/dasAllocationGroupMapper';
import { DAS_FORMULA_MENU_ACTIONS, DasFormulaRow, dasFormulaCellEditor } from './DasAllocationFormulaCells';
import { getDasAllocationFormulaColumns } from './DasAllocationFormulaColumns';

/** Warning icon whose popover lists a formula's validation errors; shown only for a formula that has some. */
function ValidationErrors({ formula }: { formula: AllocationGroupFormula }) {
    const errors = (formula.validationErrors || []).map((e, i) => <li key={i}>{e.message}</li>);
    return (
        <Popover className="errorMessagePopover" dismissButton={false} position="top" size="small" triggerType="custom"
                 content={<React.Fragment><b>Validation Errors</b><ul>{errors}</ul></React.Fragment>}>
            <Icon name="status-warning" size="normal" variant="warning" />
        </Popover>
    );
}

/**
 * Formulas grid of one Step 3 group, adapted from the TPA allocation group formulas grid; keep the two in sync.
 * The group's formulas load through the derived allocation sheet service; edits go to the reused TPA groups
 * reducer, and the section saves them. A formula may reference Step 1 descriptions and Step 2 totals, and maps
 * to the company codes of its recipient entities.
 */
export default function DasAllocationFormulasGrid({ group }: { group: AllocationGroup }) {
    const {
        services, groupsState, groupsDispatch, viewMode, sheetId, sheetVersion, dataSources, totals, companies, saving, onDuplicateFormula,
    } = useContext(DasAllocationGroupsContext);
    const editable = viewMode === CONSTANTS.VIEW_MODE.EDITABLE;
    const [formulasResult, formulasLoading, loadError] =
        services.derivedAllocationSheetService.getDASAllocationFormulas('fetch', sheetId, sheetVersion, group.allocationGroupId);
    const [rows, setRows] = useState<DasFormulaRow[]>([]);
    const [skipPageReset, setSkipPageReset] = useState(false);

    const dataSourceNames = dataSources.filter((x) => !!x.description).map((x) => ({ name: x.description }));
    const expressionParts = (expression: string) => services.readOnlyCalculationService.breakExpressionIntoParts(
        expression, totals.map((t) => ({ name: t.description, visibleSequence: t.visibleSequence })), dataSourceNames, new Set());

    useEffect(() => {
        if (formulasResult == null) {
            return;
        }
        // The reused reducer keeps formulas on the group object, as TPA does; republish the groups to render them.
        group.formulas = formulasResult.allocationFormulas.map((f, i) => toAllocationGroupFormula(f, i + 1));
        groupsDispatch(ACTIONS.SET_ALLOCATION_GROUPS.withPayload([...groupsState.groups]));
    }, [formulasResult]);

    useEffect(() => {
        if (loadError) {
            groupsDispatch(ACTIONS.SET_GROUP_LEVEL_ERROR.withPayload({ groupId: group.allocationGroupId, message: loadError }));
        }
    }, [loadError]);

    // Leaving the editable view closes every open row editor; SET_FORMULA_BEING_EDITED with no formula would not.
    useEffect(() => {
        if (!editable) {
            groupsDispatch(ACTIONS.EXIT_EDIT_MODE);
        }
    }, [viewMode]);

    useEffect(() => {
        setRows((group.formulas || []).map((f, index) => {
            f.visibleSequence = `${index + 1}`;
            const parts = expressionParts(f.expression);
            f.expressionTokens = parts;
            return {
                ...f,
                isEditable: editable,
                expressionFormatted: renderExpressionParts(parts),
                formulaOutputFormatted: services.formattingService.formatPercentage(f.value),
                mappingFormatted: (f.mapping?.values || []).join(', '),
                errorIndicator: (f.validationErrors || []).length > 0 ? <ValidationErrors formula={f} /> : null,
            };
        }));
    }, [group.formulas, totals, dataSources, viewMode]);

    const formulaOf = (row: DasFormulaRow) => (group.formulas || []).find((f) => f.allocationFormulaId === row.allocationFormulaId) || row;

    const onMenuClick = (detail: ButtonDropdownProps.ItemClickDetails, row: DasFormulaRow) => {
        const formula = formulaOf(row);
        switch (detail.id) {
            case DAS_FORMULA_MENU_ACTIONS.EDIT.id:
                groupsDispatch(ACTIONS.SET_FORMULA_BEING_EDITED.withPayload({ group, formula }));
                break;
            case DAS_FORMULA_MENU_ACTIONS.DUPLICATE.id:
                onDuplicateFormula(group, formula);
                break;
            case DAS_FORMULA_MENU_ACTIONS.REMOVE.id:
                // A never-saved formula is dropped by the reducer; a saved one asks first.
                groupsDispatch(ACTIONS.SET_REMOVE_FORMULA_PAYLOAD.withPayload({ formula }));
                break;
        }
    };

    const columnDefinitions = React.useMemo(() => getDasAllocationFormulaColumns({ viewMode, saving, onMenuClick }),
        [viewMode, saving, group.formulas]);

    const onRowUpdated = (rowIndex: number, columnId: string, value?: string | FormulaMapping) => {
        setSkipPageReset(true);
        const payload = columnId === CONSTANTS.WORKSHEET_ALLOCATION_FORMULA_FIELDS.OVERVIEW
            ? { group, field: columnId, value, extraValue: expressionParts((value as string) ?? '') }
            : { group, field: columnId, value };
        groupsDispatch(ACTIONS.UPDATE_CURRENT_FORMULA.withPayload(payload));
    };

    const onRowClicked = (e: React.MouseEvent<HTMLElement>, row: DasFormulaRow) => {
        // Clicks on the output, sequence or error cells do not start editing.
        const target = e.target as HTMLElement;
        if (!editable || !row.isEditable
            || DomUtils.isDescendantOfClassName(target, 'formulaOutputCell')
            || DomUtils.isDescendantOfClassName(target, 'formulaSequence')
            || DomUtils.isDescendantOfClassName(target, 'inlineErrorContainer')) {
            return;
        }
        groupsDispatch(ACTIONS.SET_FORMULA_BEING_EDITED.withPayload({ group, formula: formulaOf(row) }));
    };

    const addFormula = () => groupsDispatch(ACTIONS.ADD_ALLOCATION_GROUP_FORMULA.withPayload({ group, newFormula: newAllocationGroupFormula(group) }));

    return <>
        {editable && <FormulaExpressionToolbar showBuiltInFunctions={false} dispatch={groupsDispatch} />}
        <SpaceBetween size="m" direction="vertical">
            <TPELoadingSpinner loading={formulasLoading} loadingText="Loading formulas">
                <TPEReactTable {...{
                    data: rows,
                    columnDefinitions,
                    className: 'worksheetFormulasGrid',
                    sortable: false,
                    key: `dasGroupFormulasGrid-${group.allocationGroupId}`,
                    emptyMessage: editable ? 'There are no formulas in this group yet. Click Add formula to start.' : 'No formulas',
                    onRowClicked,
                    editableOptions: {
                        defaultColumn: dasFormulaCellEditor,
                        onRowUpdated,
                        skipPageReset,
                        editorsData: {
                            shouldShowValues: groupsState.showValues,
                            suggestionsList: [
                                ...dataSourceNames.map((x) => ({ name: x.name, type: 'Data set items', value: x.name })),
                                ...totals.map((t) => ({ name: t.description, type: 'Totals', value: t.description })),
                            ],
                            companiesList: companies,
                            onTextAreaCaretPositionChanged: (position: number) => groupsDispatch(ACTIONS.SET_EDITOR_CARET_POSITION.withPayload(position)),
                        },
                    },
                }} />
            </TPELoadingSpinner>
            {editable && (
                <Button data-class="subPrimaryButton" className={`dasAddFormula${group.allocationGroupId}`} onClick={addFormula}
                        disabled={saving || formulasLoading}>Add formula</Button>
            )}
        </SpaceBetween>
    </>;
}
